"use client";

import type { LanguageCode } from "@sum-video/shared/types/language";
import { usePathname, useSearchParams } from "next/navigation";
import { type JSX, Suspense, useEffect, useRef } from "react";
import { useAuthAnalytics } from "@/hooks/useAuthAnalytics";
import { isAnalyticsHost } from "@/lib/analytics/analyticsHost";

function getReactPixel() {
  if (typeof window === "undefined") return null;
  // Use require() to avoid SSR evaluation - only loads when function is called
  return require("react-facebook-pixel").default;
}

/**
 * Client component that handles authentication analytics
 * This should be included in the layout to ensure user identification
 * happens globally across the application
 */
export function AnalyticsProvider({
  locale,
}: {
  locale: LanguageCode;
}): JSX.Element {
  useAuthAnalytics(locale);
  return (
    <Suspense fallback={null}>
      <AnalyticsPixelTracker />
    </Suspense>
  );
}

function AnalyticsPixelTracker(): JSX.Element {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const pixelReady = useRef(false);

  // The pixel downloads Meta's fbevents.js, so it starts only after the page
  // finished loading; the init records the first page view.
  useEffect(() => {
    if (!isAnalyticsHost()) return;
    const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID;
    if (!pixelId) return;
    const init = (): void => {
      const ReactPixel = getReactPixel();
      if (!ReactPixel) return;
      ReactPixel.init(pixelId);
      ReactPixel.pageView();
      ReactPixel.track("ViewContent");
      pixelReady.current = true;
    };
    if (document.readyState === "complete") {
      init();
      return;
    }
    window.addEventListener("load", init, { once: true });
    return () => window.removeEventListener("load", init);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: a query-only navigation is a new page view too
  useEffect(() => {
    if (!pixelReady.current || !pathname) return;
    const ReactPixel = getReactPixel();
    if (!ReactPixel) return;
    // Trigger on route changes
    ReactPixel.pageView();
    ReactPixel.track("ViewContent");
  }, [pathname, searchParams]);

  return <></>;
}
